import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { matchPriceBookItemService } from '@/api/match';

interface InvoiceItem {
  id: string;
  description: string;
  item_code: string | null;
  unit_price: number | null;
  discount: number | null;
}

interface PriceBookItem {
  id: string;
  description: string;
  itemcode: string;
  price: number;
  discount: number;
}

interface MatchResult {
  price_book_item_id: string;
  item_code: string;
  description: string;
  price: number;
  discount: number;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
}

interface PriceBookMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceItem: InvoiceItem | null;
  priceBookItems: PriceBookItem[];
}

const confidenceColors: Record<string, string> = {
  high: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  low: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
};

export function PriceBookMatchDialog({ open, onOpenChange, invoiceItem, priceBookItems }: PriceBookMatchDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matches, setMatches] = useState<MatchResult[]>([]);
  const [noMatchExplanation, setNoMatchExplanation] = useState<string | null>(null);

  const fetchMatches = async () => {
    if (!invoiceItem) return;

    setLoading(true);
    setError(null);
    setMatches([]);
    setNoMatchExplanation(null);

    try {
      const data = await matchPriceBookItemService.match(
        {
          item_code: invoiceItem.item_code,
          description: invoiceItem.description,
          unit_price: invoiceItem.unit_price,
          discount: invoiceItem.discount,
        },
        priceBookItems.map((pb) => ({
          id: pb.id,
          itemcode: pb.itemcode,
          description: pb.description,
          price: pb.price,
          discount: pb.discount,
        })),
      );

      if (!data.success) throw new Error(data.error || 'Analysis failed');

      setMatches(data.matches || []);
      setNoMatchExplanation(data.no_match_explanation || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && invoiceItem) {
      fetchMatches();
    }
  }, [open, invoiceItem?.id]);

  const formatCurrency = (val: number | null) => {
    if (val === null || val === undefined) return '—';
    return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const invoiceNetPrice = invoiceItem?.unit_price != null
    ? invoiceItem.unit_price * (1 - (invoiceItem.discount ?? 0) / 100)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Price Book Match Analysis</DialogTitle>
          <DialogDescription>
            AI analysis of whether this non-contract item matches an existing price book entry.
          </DialogDescription>
        </DialogHeader>

        {invoiceItem && (
          <div className="rounded-md border border-border bg-muted/30 p-3 text-sm mb-4">
            <p className="font-medium mb-1">Invoice Item</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
              <span>Code: <span className="font-mono text-foreground">{invoiceItem.item_code || 'N/A'}</span></span>
              <span>Price: <span className="text-foreground">{formatCurrency(invoiceItem.unit_price)}</span></span>
              <span className="col-span-2">Description: <span className="text-foreground">{invoiceItem.description}</span></span>
              <span>Discount: <span className="text-foreground">{invoiceItem.discount ?? 0}%</span></span>
              <span>Net Price: <span className="text-foreground">{formatCurrency(invoiceNetPrice)}</span></span>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin" />
            <p className="text-sm">Analyzing item against price book...</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-3 py-8">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm font-medium">Analysis failed</p>
            </div>
            <p className="text-sm text-muted-foreground text-center">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchMatches}>
              <RefreshCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          </div>
        )}

        {!loading && !error && matches.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Found {matches.length} potential match{matches.length !== 1 ? 'es' : ''}:
            </p>
            {matches.map((match, i) => {
              const pbNetPrice = match.price * (1 - match.discount / 100);
              const priceDiff = invoiceNetPrice != null ? invoiceNetPrice - pbNetPrice : null;

              return (
                <div key={match.price_book_item_id || i} className="rounded-md border border-border p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm">{match.item_code}</span>
                        <Badge className={confidenceColors[match.confidence]}>
                          {match.confidence}
                        </Badge>
                      </div>
                      <p className="text-sm">{match.description}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-xs border-t border-border pt-2">
                    <div>
                      <span className="text-muted-foreground">PB Price:</span>{' '}
                      <span className="font-medium">{formatCurrency(match.price)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">PB Discount:</span>{' '}
                      <span className="font-medium">{match.discount}%</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">PB Net:</span>{' '}
                      <span className="font-medium">{formatCurrency(pbNetPrice)}</span>
                    </div>
                  </div>

                  {priceDiff !== null && Math.abs(priceDiff) > 0.01 && (
                    <div className={`text-xs px-2 py-1 rounded ${priceDiff > 0 ? 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300' : 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300'}`}>
                      Invoice net price is {formatCurrency(Math.abs(priceDiff))} {priceDiff > 0 ? 'higher' : 'lower'} than price book
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground italic">{match.reasoning}</p>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && matches.length === 0 && noMatchExplanation && (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground mb-2">No matching price book items found.</p>
            <p className="text-sm">{noMatchExplanation}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
